import { ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { BreadcrumbItem } from '@/lib/api-client';

interface FolderBreadcrumbsProps {
  breadcrumbs: BreadcrumbItem[];
  onNavigate: (folderId: string | null) => void;
  className?: string;
}

export function FolderBreadcrumbs({
  breadcrumbs,
  onNavigate,
  className,
}: FolderBreadcrumbsProps) {
  // The folder one level up - the root when this is a top-level folder.
  const parent =
    breadcrumbs.length >= 2 ? breadcrumbs[breadcrumbs.length - 2] : null;
  return (
    <>
      {/* Phone: one step back, not the whole trail. The page title already
          names the current folder, and a full trail at 390px wrapped one
          word per line. */}
      <button
        type='button'
        onClick={() => onNavigate(parent?.folder_id ?? null)}
        className='tap-target -ml-2 inline-flex max-w-full items-center gap-1 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:hidden'
      >
        <ChevronLeft className='h-4 w-4 flex-shrink-0' />
        <span className='truncate'>{parent?.name ?? 'My Learning'}</span>
      </button>
      <nav
        aria-label='Folder breadcrumbs'
        className={cn(
          'hidden flex-wrap items-center gap-1 text-sm text-muted-foreground md:flex',
          className
        )}
      >
        <button
          onClick={() => onNavigate(null)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors',
            'hover:bg-muted hover:text-foreground',
            breadcrumbs.length === 0 && 'text-foreground'
          )}
        >
          <Home className='h-3.5 w-3.5' />
          My Learning
        </button>
        {breadcrumbs.map((crumb, idx) => {
          const isLast = idx === breadcrumbs.length - 1;
          return (
            <span
              key={crumb.folder_id}
              className='inline-flex items-center gap-1'
            >
              <ChevronRight className='h-3.5 w-3.5 text-muted-foreground' />
              <button
                onClick={() => onNavigate(crumb.folder_id)}
                disabled={isLast}
                className={cn(
                  'rounded-md px-2 py-1 transition-colors',
                  !isLast && 'hover:bg-muted hover:text-foreground',
                  isLast && 'cursor-default font-medium text-foreground'
                )}
              >
                {crumb.name}
              </button>
            </span>
          );
        })}
      </nav>
    </>
  );
}
